import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { AppIcon } from '../ui/icon'
import { mdiLockOutline, mdiShareVariantOutline } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { useCan, type PermissionBearingEntity } from '../../contexts/PermissionsContext'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { threadTitleT } from '../conversations/thread'

export interface DocumentConversationPrivateBannerConversation {
    uid?: string
    message_uid?: string | null
    /**
     * The thread's RESOLVED name, as `DocumentConversationRef` now sends it
     * (spec 2026-09-13-02). It is NOT the carrier's `Subject:` header — that
     * field never crossed this boundary, it only used to be called `subject`.
     */
    title?: string | null
}

interface DocumentConversationPrivateBannerProps {
    org: string
    docUid: string
    /** The threads this document is attached to — the first one names the banner. */
    conversations?: DocumentConversationPrivateBannerConversation[] | null
    /** The document itself, for its per-row `permissions` array. */
    document?: PermissionBearingEntity | null
}

/**
 * Banner shown on a document that is still private to the conversation it
 * arrived on (spec 2026-09-12-01 § 3).
 *
 * It is the ONLY place a manager learns why this file is absent from the
 * library, the asset's folder tree and search — so it says which thread owns
 * it, links there, and offers the one-way `Share with organization` action
 * behind an AlertDialog (the action cannot be undone; a mis-click would publish
 * a resident's photo to every member).
 *
 * Rendered only when the caller has already checked the flag.
 */
export function DocumentConversationPrivateBanner({
    org,
    docUid,
    conversations,
    document,
}: DocumentConversationPrivateBannerProps) {
    const { t } = useTranslation('documents')
    const queryClient = useQueryClient()
    const [confirmOpen, setConfirmOpen] = useState(false)
    // The banner itself is always readable — a manager who cannot share still
    // needs to know why this file is missing from the library — but the action
    // is `documents.write` on THIS row (spec 2026-09-12-01 § 3). Showing an
    // irreversible-looking button that 403s is worse than showing none.
    const canShare = useCan('documents.write', document)

    const thread = conversations?.[0]
    const title = threadTitleT(thread, t, 'conversations.no_subject')

    const shareMutation = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/share-with-organization',
        {
            onSuccess: () => {
                setConfirmOpen(false)
                // The document itself (the banner must disappear) and every
                // list it now belongs to.
                queryClient.invalidateQueries({
                    predicate: (query) => {
                        const key = query.queryKey as any[]
                        if (key.length < 2 || key[0] !== 'get') return false
                        const path = key[1]
                        return (
                            path === '/api/v1/organizations/{orgCode}/documents/{documentUid}' ||
                            path === '/api/v1/organizations/{orgCode}/documents' ||
                            path === '/api/v1/organizations/{orgCode}/conversations/{conversationUid}'
                        )
                    },
                })
            },
        },
    )

    const share = () => {
        shareMutation.mutate({
            params: { path: { orgCode: org, documentUid: docUid } },
        })
    }

    const shareLabel = t('conversation_private.share')

    return (
        <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3"
            data-testid="document-conversation-private-banner"
        >
            <div className="flex min-w-0 items-center gap-2 text-sm">
                <AppIcon path={mdiLockOutline} size="md" className="shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="min-w-0">
                    {thread?.uid ? (
                        <>
                            {t('conversation_private.banner_prefix')}{' '}
                            <Link
                                to="/$org/conversations"
                                params={{ org }}
                                search={{
                                    status: undefined,
                                    channel: undefined,
                                    resolution: undefined,
                                    asset: undefined,
                                    unattached: undefined,
                                    draft: undefined,
                                    q: undefined,
                                    selected: undefined,
                                    reply_for_issue: undefined,
                                    conversation: String(thread.uid),
                                    message: thread.message_uid ? String(thread.message_uid) : undefined,
                                }}
                                className="text-primary hover:underline"
                                data-testid="document-conversation-private-thread-link"
                            >
                                {title}
                            </Link>
                        </>
                    ) : (
                        t('conversation_private.banner_no_thread')
                    )}
                    <span className="block text-muted-foreground">
                        {t('conversation_private.explanation')}
                    </span>
                </span>
            </div>
            {canShare && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmOpen(true)}
                    disabled={shareMutation.isPending}
                    aria-label={shareLabel}
                    title={shareLabel}
                    data-testid="document-share-with-organization"
                >
                    <AppIcon path={mdiShareVariantOutline} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{shareLabel}</span>
                </Button>
            )}

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="document-share-with-organization-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('conversation_private.confirm_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('conversation_private.confirm_description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="document-share-with-organization-cancel">
                            {t('conversation_private.confirm_cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={share}
                            data-testid="document-share-with-organization-confirm"
                        >
                            {shareLabel}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
